
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const translations = {
  en: {
    title: "Study Destinations",
    subtitle: "Choose Your Path to Success",
    uk: {
      title: "United Kingdom",
      description: "Study at prestigious UK universities and benefit from world-class education and diverse cultural experiences.",
      features: ["Top 100 Universities", "Post-Study Work Visa", "Multicultural Environment"]
    },
    malaysia: {
      title: "Malaysia",
      description: "Experience quality education in Malaysia's modern universities with affordable living costs and Islamic-friendly environment.",
      features: [
        "UTM - Universiti Teknologi Malaysia",
        "UPM - Universiti Putra Malaysia",
        "UM - University of Malaya",
        "Taylor's University"
      ]
    },
    cta: "Learn More"
  },
  // ... other translations remain the same
};

function Destinations({ language }) {
  const content = translations[language];

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{content.title}</h2>
          <p className="text-lg text-muted-foreground">{content.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60 rounded-lg"></div>
            <img 
              src="https://storage.googleapis.com/hostinger-horizons-assets-prod/2035fa37-35aa-4837-b61a-e7175478d8be/4efd570785cb9f4fc951098362522893.png" 
              alt="Students at UK campus" 
              className="w-full h-[400px] object-cover rounded-lg"
            />
            <div className="absolute bottom-0 left-0 p-6 text-white">
              <h3 className="text-2xl font-bold mb-2">{content.uk.title}</h3>
              <p className="mb-4">{content.uk.description}</p>
              <ul className="mb-4 space-y-2">
                {content.uk.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-accent rounded-full"></span>
                    {feature}
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-primary group">
                {content.cta}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60 rounded-lg"></div>
            <img 
              src="https://storage.googleapis.com/hostinger-horizons-assets-prod/2035fa37-35aa-4837-b61a-e7175478d8be/15514b9aa8b14027773048c2b9e61644.webp" 
              alt="Modern Malaysian university campus" 
              className="w-full h-[400px] object-cover rounded-lg"
            />
            <div className="absolute bottom-0 left-0 p-6 text-white">
              <h3 className="text-2xl font-bold mb-2">{content.malaysia.title}</h3>
              <p className="mb-4">{content.malaysia.description}</p>
              <ul className="mb-4 space-y-2">
                {content.malaysia.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-accent rounded-full"></span>
                    {feature}
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-primary group">
                {content.cta}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default Destinations;
